import { useEffect, useRef, useState } from 'react';
import { Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useGetInvestigationTimerQuery } from '@/store/api/apiSlice';
import type { SlaTimerStatus } from '@/types';

interface SlaCountdownProps {
  investigationId: string;
  // Poll cadence for re-fetching the timer from the server.
  // The tick clock runs locally between polls.
  pollSeconds?: number;
  className?: string;
}

const STAGE_LABEL: Record<string, string> = {
  assignment: 'Assignment SLA',
  fieldwork: 'Fieldwork SLA',
  review: 'Review SLA',
};

const STATUS_STYLES: Record<SlaTimerStatus, { box: string; pill: string; icon: typeof Clock }> = {
  on_track: {
    box: 'bg-green-50 border-green-200',
    pill: 'bg-green-100 text-green-700 border-green-200',
    icon: Clock,
  },
  urgent: {
    box: 'bg-amber-50 border-amber-200',
    pill: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
  },
  overdue: {
    box: 'bg-red-50 border-red-200',
    pill: 'bg-red-100 text-red-700 border-red-200',
    icon: AlertTriangle,
  },
  inactive: {
    box: 'bg-gray-50 border-gray-200',
    pill: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: CheckCircle2,
  },
};

function formatDuration(ms: number): string {
  const abs = Math.abs(ms);
  const second = 1000;
  const minute = 60 * second;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs >= day) {
    const days = Math.floor(abs / day);
    const hours = Math.floor((abs % day) / hour);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (abs >= hour) {
    const hours = Math.floor(abs / hour);
    const mins = Math.floor((abs % hour) / minute);
    return `${hours}h ${mins.toString().padStart(2, '0')}m`;
  }
  if (abs >= minute) {
    const mins = Math.floor(abs / minute);
    const secs = Math.floor((abs % minute) / second);
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  }
  return `${Math.max(0, Math.floor(abs / second))}s`;
}

export default function SlaCountdown({
  investigationId,
  pollSeconds = 45,
  className = '',
}: SlaCountdownProps) {
  const { data, isLoading, isError } = useGetInvestigationTimerQuery(investigationId, {
    pollingInterval: pollSeconds * 1000,
  });

  // Capture the server/client clock offset once per fetch so the countdown
  // stays anchored to the server even if the client clock drifts. We refresh
  // the offset whenever a fresh `serverTime` arrives from the API.
  const offsetRef = useRef(0);
  useEffect(() => {
    if (!data?.serverTime) return;
    offsetRef.current = Date.parse(data.serverTime) - Date.now();
  }, [data?.serverTime]);

  // Local 1s tick so the displayed remaining time advances between polls.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!data?.deadline) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [data?.deadline]);

  // remaining = deadline - (clientNow + offset)  where offset = serverNow - clientNow at fetch time
  const remainingMs = data?.deadline
    ? Date.parse(data.deadline) - (Date.now() + offsetRef.current)
    : null;

  if (isLoading) {
    return (
      <div
        className={`rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500 ${className}`}
      >
        Loading SLA timer…
      </div>
    );
  }

  if (isError || !data) return null;

  // Don't render anything for stages where there's no active SLA — keeps the
  // detail page clean for completed/cancelled investigations.
  if (data.timerStatus === 'inactive' || !data.deadline) {
    return null;
  }

  const styles = STATUS_STYLES[data.timerStatus] ?? STATUS_STYLES.on_track;
  const Icon = styles.icon;
  const stageLabel = (data.stage && STAGE_LABEL[data.stage]) || 'SLA';
  const isOverdue = data.timerStatus === 'overdue' || (remainingMs !== null && remainingMs < 0);
  const displayMs = remainingMs ?? (data.remainingSeconds ?? 0) * 1000;

  const deadlineLocal = new Date(data.deadline).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className={`rounded-lg border ${styles.box} px-4 py-3 flex items-center justify-between gap-4 flex-wrap ${className}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon className="w-5 h-5 shrink-0" />
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-semibold text-gray-700">
            {stageLabel}
          </p>
          <p className="text-sm text-gray-900 truncate">{data.message}</p>
        </div>
      </div>
      <div className="flex flex-col items-end shrink-0">
        <span
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md border ${styles.pill}`}
        >
          {isOverdue ? 'Overdue by' : 'Due in'} {formatDuration(displayMs)}
        </span>
        <span className="text-[11px] text-gray-500 mt-1">Deadline: {deadlineLocal}</span>
      </div>
    </div>
  );
}
